import {ErrorHandler, Injectable} from "@angular/core";

const RELOAD_TIMESTAMP_KEY = "chunk-reload-timestamp";
const RELOAD_WINDOW_MS = 10_000;

/**
 * Detects the errors a browser throws when a hashed lazy chunk can no longer be
 * fetched — the signature of a client still holding a pre-deploy `index.html`.
 */
export function isChunkLoadError(error: unknown): boolean {
    const name = (error as {name?: string})?.name ?? "";
    const message = (error as {message?: string})?.message ?? String(error);

    return (
        name === "ChunkLoadError" ||
        /Loading chunk \S+ failed/i.test(message) ||
        /Failed to fetch dynamically imported module/i.test(message) ||
        /error loading dynamically imported module/i.test(message) ||
        /Importing a module script failed/i.test(message)
    );
}

/**
 * Forces a single hard reload to pull the current `index.html` (served
 * `no-cache`, see staticwebapp.config.json) and the matching chunk hashes.
 * A short reload window guards against an infinite loop while still letting the
 * app self-heal against a genuinely new deploy later in the same session.
 *
 * @returns true if a reload was triggered, false if suppressed by the guard.
 */
export function reloadOnceForChunkError(): boolean {
    const now = Date.now();
    const lastReload = Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) ?? "0");

    if (now - lastReload > RELOAD_WINDOW_MS) {
        sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(now));
        window.location.reload();
        return true;
    }

    // Reloaded moments ago and the chunk still failed — stop here instead of
    // looping so the user isn't trapped in a reload cycle.
    console.error("Chunk load failed again after a recent reload; aborting auto-reload.");
    return false;
}

/**
 * Recovers from stale-deploy chunk-load failures at runtime.
 *
 * The app is built with `outputHashing: "all"`, so every deploy produces lazy
 * chunks with new hashed filenames. A browser still holding an old `index.html`
 * requests chunk filenames that no longer exist under /static/ and gets a 404 —
 * the login loader hangs, or a freshly-opened /quotes/:id tab renders blank.
 * Reloading fetches the current shell and the chunks it references.
 */
@Injectable()
export class ChunkErrorHandler implements ErrorHandler {
    handleError(error: unknown): void {
        if (isChunkLoadError(error)) {
            reloadOnceForChunkError();
            return;
        }

        console.error(error);
    }
}
